import { DropdownListItem } from '@rsos/components/capstone/base/Dropdown/DropdownComponents';
import styled, { Theme } from '@rsos/theme/styled';

// Wrapper around both Caller controls and Unite controls
export const StyledControls = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin: 10px;
`;

export const StyledControlsSection = styled.div`
  display: flex;
  padding: 8px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 4px;
  border-radius: 10.345px;
  background: rgba(17, 17, 17, 0.7);
  color: #fff;
  backdrop-filter: blur(1.3px);
`;

export const StyledButtonRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  gap: 8px;
`;

/* The "normal" buttons are ~24px squares and the icons size themselves to preserve aspect ratio */
export const StyledButton = styled.div<{
  btnType?: keyof Theme['colors']['buttons'];
  disabled?: boolean;
}>`
  cursor: ${({ disabled = false }) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled = false }) => (disabled ? '0.3' : '1')};
  margin: 0px 0px;
  width: 24px;
  height: 24px;
  min-width: 24px;
  min-height: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 4px;
  background: ${({ theme, btnType = 'secondary' }) =>
    theme.colors.buttons[btnType].bg};
  border: 0.5px solid
    ${({ theme, btnType = 'secondary' }) =>
      btnType === 'secondary' ? theme.colors.heavyLineOutline : 'transparent'};
  border-radius: 5px;

  &:hover {
    background: ${({ theme, btnType = 'secondary' }) =>
      theme.colors.buttons[btnType].hover};
  }
  /*
  .lk-button is a live-kit button that is within some of our components. We basically style it to make it as if it isn't there
  */
  .lk-button {
    cursor: pointer;
    border: 0px;
    padding: 0px;
    background: transparent;

    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;

    /* the svg icons inherit this color */
    color: ${({ theme }) => theme.colors.primaryText};
  }

  /* the svg icons inherit this color */
  color: ${({ theme }) => theme.colors.primaryText};

  svg {
    max-width: 100%;
    max-height: 100%;
    display: block;
  }
`;

/** Forms a split button so that the left child is a normal StyledButton right child is the dropdown portion */
export const StyledSplitButton = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  gap: 0px;
  border-radius: 5px;
  border: 0.5px solid ${({ theme }) => theme.colors.heavyLineOutline};

  /* Left portion (main button) gets rounded corners on left */
  > div:first-child {
    border: none;
    border-radius: 5px 0px 0px 5px;
  }

  /* 
    Right portion (dropdown) gets rounded corners on right.
    It is a LiveKit button and a dropdown menu within our split button, so we do some extensive styling here.
  */
  > button.lk-button.lk-button-menu {
    border: none;
    border-radius: 0px 5px 5px 0px;
    width: 16px;
    min-width: 24px;
    min-height: 24px;
    color: ${({ theme }) => theme.colors.primaryText};
    background: ${({ theme }) => theme.colors.buttons['secondary'].bg};

    /* 
      We want the up chevron by default and the down chevron when the menu is open:
      div:first-child -> up chevron.
      div:last-child  -> down chevron.
      We disable pointer events on the icons so that the containing button registers the click.
    */
    &[aria-pressed='false'] > div:first-child {
      display: block;
      pointer-events: none;
    }
    &[aria-pressed='false'] > div:last-child {
      display: none;
      pointer-events: none;
    }
    &[aria-pressed='true'] > div:first-child {
      display: none;
      pointer-events: none;
    }
    &[aria-pressed='true'] > div:last-child {
      display: block;
      pointer-events: none;
    }
  }

  /* LiveKit device menu contained within the our split button */
  .lk-device-menu {
    width: max-content;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 5;
    min-width: 180px;
    padding: 10px 0px;
    white-space: nowrap;
    border-radius: 0.75rem;
    background: ${({ theme }) => theme.colors.buttons['secondary'].bg};
  }

  /* The ULs have margin by default that we don't want */
  .lk-device-menu ul.lk-media-device-select {
    margin-top: 0px;
    margin-bottom: 0px;
  }

  /* the first UL (inputs) is separated from the second (outputs) by a border */
  .lk-device-menu ul.lk-media-device-select:first-of-type {
    border-bottom: ${({ theme }) =>
      `1px solid ${theme.colors.lightLineDivider}`};
  }

  /* menu items within the LiveKit device menu */
  .lk-media-device-select {
    padding-bottom: 8px;
    margin-bottom: 0px;

    max-height: 135px;
    overflow-y: auto;

    & li {
      min-height: 45px;

      padding: 12px 16px;
      position: relative;
      color: ${({ theme }) => theme.colors.secondaryLink};

      &:hover {
        & .lk-button {
          text-overflow: clip;
          white-space: normal;
          word-break: break-all;
        }
      }

      & .lk-button {
        text-overflow: ellipsis;
        max-width: 265px;
        white-space: nowrap;
        overflow: hidden;
        text-align: left;

        padding: 0px;
        width: 100%;
        height: auto;
        min-height: 20px;

        /* Reserve space for checkmark on the left */
        padding-left: 24px;
        color: ${({ theme }) => theme.colors.primaryText};

        cursor: pointer;
        border: 0px;
        background: transparent;

        width: 100%;
        height: 100%;

        /* the svg icons inherit this color */
        color: ${({ theme }) => theme.colors.primaryText};
      }
    }

    /* Add checkmark for active items */
    & li[data-lk-active='true']::before {
      content: '✔️';
      position: absolute;
      left: 16px;
      top: 50%;
      transform: translateY(-50%);
      font-size: 14px;
      font-weight: bold;
    }

    & li:hover {
      background: ${({ theme }) => theme.colors.highlights};
      cursor: pointer;
    }
  }

  .lk-media-device-select [data-lk-active='true'] > .lk-button {
    /*color: var(--lk-accent-fg);*/
    /*background-color: var(--lk-accent-bg);*/
  }

  /* LiveKit's menu renders headers for the two seconds and our designs don't call for them */
  .lk-device-menu .lk-device-menu-heading {
    display: none;
  }
`;

export const DeviceDropdownListMenuContainer = styled.div`
  position: absolute;
  width: 180px;
  background: ${({ theme }) => theme.colors.background};
`;

/** A DropdownListItem that contains a label, an optional icon, and an optional "System Default" label on the bottom */
export const DeviceDropdownListItem = styled(DropdownListItem)`
  display: flex;
  align-items: center;
  gap: 8px;
`;
